var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { isAfter, parseISO } from "date-fns";
import { ConsentDataSensitivityScope } from "healthex-common/consentscopes.dtos";
import { isErr } from "healthex-common/result";
import { ScreenshotMetadataStatus } from "healthex-common/screenshot.dtos";
import { ProjectType, StudyImportMethod, StudyInvestigationType, StudyPeriodicUnit, StudyTranslationLanguage, StudyVersionStatus, StudyPeriodicDayAndWeekUnits, } from "healthex-common/study.dtos";
import { TranscodeInput, TranscodeStatus } from "healthex-common/transcode.dtos";
import { filterNull, indexBy, omit, pick, toISODateString, withoutUndefinedProperties } from "healthex-common/utils";
import { computed, observable, reaction, transaction, when } from "mobx";
import { NOT_LOADED } from "../../Infra/RemoteData";
import { validators } from "../../Logic/Validators";
import { downcastFormModel, FormModel } from "../FormModel";
import { NamedRoutes } from "../NamedRoutes";
import { findMatchingNamedRouteParent, routeEquals } from "../RoutingUIManager";
import { ProjectKey } from "healthex-common/project.data.dtos";
import { getProjectKey } from "healthex-common/project.data.utils";
export class AddProjectFormUIManager {
    constructor(studyManager, studyService, routingUIManager, authManager, provisioningService) {
        this.studyManager = studyManager;
        this.studyService = studyService;
        this.routingUIManager = routingUIManager;
        this.authManager = authManager;
        this.provisioningService = provisioningService;
        this.study = NOT_LOADED;
        this.studyExistsButHasNoDraft = NOT_LOADED;
        this.versionNumber = NOT_LOADED;
        this.enrollmentVideoUploadingInfo = null;
        this.baseStudy = {};
        this._selectedTranslationLanguage = null;
        this.studyFormModel = new FormModel({
            importMethod: null,
            studyType: null,
            accessData: null,
            projectType: ProjectType.GENERAL_DATA_USE,
            title: "",
            briefSummary: "",
            description: "",
            clinicalTrialPhase: "",
            primaryDiseaseOrConditions: [""],
            interventions: [""],
            outcomes: "",
            inclusionExclusionCriteria: "",
            fullRecordWithoutRestrictedData: null,
            consentDataResourceScopes: [],
            consentDataSensitivityScopes: [],
            targetNumberOfPatients: "",
            startDate: "",
            completionDate: "",
            primaryCompletionDate: "",
            healthRecordsUpdatesEnabled: null,
            healthRecordsUpdatesOnDemand: null,
            healthRecordsUpdatesEvery: "",
            healthRecordsUpdatesUnit: null,
            sponsorOrganizationName: "",
            sponsorPrimaryContactName: "",
            sponsorPrimaryContactTitle: "",
            sponsorPrimaryContactEmail: "",
            sponsorPrimaryContactPhone: "",
            sponsorPrimaryContactPhoneExt: "",
            funderType: [],
            studyIdentifier: "",
            webpageLink: "",
            protocolNumber: "",
            defaultRequire21CFRPart11ForInformedConsents: null,
            isPrescreenConsentRequired: null,
            icfImportMethod: null,
            icfFileName: "",
            studyProtocolFileName: "",
            patientFriendlyTitle: "",
            patientFriendlySummary: "",
            purposeOfResearchText: "",
            benefitsOfEnrollment: "",
            risksOfEnrollment: "",
            proceduresAndInterventionsText: "",
            timelineVisitsEnabled: null,
            timelineVisits: [
                { period: "", description: "" },
                { period: "", description: "" },
            ],
            alternativeProceduresAndTreatments: "",
            injuryProtocolText: "",
            leavingStudyEarlyEnabled: null,
            leavingStudyEarlyText: "",
            participationTerminationEnabled: null,
            participationTerminationText: "",
            futureUseOfDataEnabled: null,
            futureUseOfData: null,
            futureUseOfDataText: "",
            confidentialityOfDataText: "",
            compensation: null,
            compensationText: "",
            costOfParticipationEnabled: null,
            costOfParticipationText: "",
            sharingResultsEnabled: null,
            sharingResultsText: "",
            additionalSections: [
                { id: crypto.randomUUID(), title: "", description: "", included: true, language: StudyTranslationLanguage.en },
            ],
            sitePrimaryContactName: "",
            sitePrimaryContactTitle: "",
            sitePrimaryContactEmail: "",
            sitePrimaryContactPhone: "",
            sitePrimaryContactPhoneExt: "",
            sitePrimaryContactLink: "",
            hipaaAuthorizationText: "",
            hipaaAuthorizationTemplateEnabled: true,
            hipaaAuthorizationForm: null,
            lastSkippedGenaiInputHash: "",
            patientMRNOnInformedConsent: false,
            orgLogoOnInformedConsent: null,
            organizationIconUrl: null,
            enrollmentVideo: null,
            deletePrescreenTranscode: null,
            deleteEnrollmentTranscode: null,
            icfTextSegments: [],
            studySectionImages: [],
            investigationType: StudyInvestigationType.NEITHER,
            purposeOfResearchEnabled: null,
            informedConsentPrimaryDiseaseOrConditions: [],
            informedConsentIRBApprovalStamp: "",
            informedConsentStartDate: "",
            informedConsentCompletionDate: "",
            informedTargetNumberOfPatients: "",
            alternativesToParticipation: "",
            informedConsentInterested: false,
            loginTitle: "",
            loginText: "",
            welcomeTitle: "",
            welcomeText: "",
            dataUpdateWelcomeTitle: "",
            dataUpdateWelcomeText: "",
            californiaBillOfRightsTitle: "",
            californiaBillOfRightsText: "",
            studyInformationTitle: "",
            purposeOfResearchTitle: "",
            lengthOfStudyNumberOfParticipantsTitle: "",
            lengthOfStudyNumberOfParticipantsText: "",
            proceduresAndInterventionsTitle: "",
            timelineTitle: "",
            benefitsTitle: "",
            risksTitle: "",
            voluntaryParticipationTitle: "",
            voluntaryParticipationText: "",
            withdrawalImplicationsTitle: "",
            injuryProtocolTitle: "",
            participationTerminationTitle: "",
            dataTypesTitle: "",
            sensitiveDataTitle: "",
            sensitiveDataText: "",
            futureUseOfDataTitle: "",
            confidentialityMeasuresTitle: "",
            compensationTitle: "",
            costTitle: "",
            researchResultsTitle: "",
            whoToContactTitle: "",
            whoToContactText: "",
            informedConsentTitle: "",
            informedConsentText: "",
            informedConsentYesText: "",
            informedConsentNoText: "",
            informedConsentInterestedText: "",
            hipaaTitle: "",
            hipaaText: "",
            hipaaOrganizationLegalName: "",
            hipaaOrganizationLegalNameConfirmed: false,
            thankYouConsentedTitle: "",
            thankYouConsentedText: "",
            dataUpdateThankYouConsentedTitle: "",
            dataUpdateThankYouConsentedText: "",
            thankYouDeclinedTitle: "",
            thankYouDeclinedText: "",
            thankYouImInterestedTitle: "",
            thankYouImInterestedText: "",
            informedConsentPrimaryDiseaseOrConditionsCopied: [],
            informedConsentStartDateCopied: "",
            informedConsentCompletionDateCopied: "",
            informedTargetNumberOfPatientsCopied: "",
            lengthOfStudyEnabled: null,
            informedHealthRecordUpdatesEnabled: null,
            healthRecordUpdatesTitle: "",
            healthRecordUpdatesText: "",
            injuryProtocolEnabled: null,
            interventionsEnabled: null,
            confidentialityMeasuresEnabled: null,
            patientMRNOnParticipantSignature: null,
            minorParticipantConsentEnabled: false,
            additionalParentGuardianEnabled: false,
            additionalParentGuardianExemptSignature: true,
            minorParticipantExemptFromAssent: false,
            larConsentEnabled: false,
            larParticipantConsentExemptFromAssent: true,
            parentGuardianTitle: "",
            parentGuardianInstructions: "",
            parentGuardianSignatureTitle: "",
            parentGuardianInformedConsentStatement: "",
            additionalParentGuardianTitle: "",
            additionalParentGuardianInstructions: "",
            additionalParentGuardianExemptSignatureText: "",
            additionalParentGuardianSignatureTitle: "",
            additionalParentGuardianInformedConsentStatement: "",
            minorParticipantTitle: "",
            minorParticipantInstructions: "",
            minorParticipantExemptFromAssentText: "",
            minorParticipantAssentTitle: "",
            minorParticipantAssentStatement: "",
            participantTitle: "",
            participantInstructionsForGuidedConsent: "",
            informedConsentStatement: "",
            larTitle: "",
            larInstructions: "",
            larSignatureTitle: "",
            larInformedConsentStatement: "",
            larParticipantTitle: "",
            larParticipantInstructions: "",
            larParticipantExemptFromAssentText: "",
            larParticipantAssentTitle: "",
            larParticipantAssentStatement: "",
            witnessTitle: "",
            witnessInstructions: "",
            witnessSignatureTitle: "",
            witnessConfirmation: "",
            providerTitle: "",
            providerInstructions: "",
            providerSignatureTitle: "",
            providerConfirmation: "",
            studyTranslatableFields: null,
            suppressNotifications: false,
            introTitle: "",
            introPurposeOfDisclosure: "",
            introBriefEducation: "",
            patientHealthRecordRightsTitle: "",
            contactDetails: "",
            purposeOfDataTitle: "",
            purposeOfDataDisclosure: [],
            purposeOfDataDisclosureOther: "",
            purposeOfDataBriefEducation: "",
            patientRecordRetrievalRecencyEnabled: null,
            patientRecordRetrievalRecencyEvery: "",
            patientRecordRetrievalRecencyUnit: null,
        }, [
            "title",
            "briefSummary",
            "primaryDiseaseOrConditions",
            "interventions",
            "outcomes",
            "inclusionExclusionCriteria",
            "fullRecordWithoutRestrictedData",
            "consentDataResourceScopes",
            "targetNumberOfPatients",
            "startDate",
            "completionDate",
            "primaryCompletionDate",
            "healthRecordsUpdatesEnabled",
            "healthRecordsUpdatesOnDemand",
            "sponsorOrganizationName",
            "sponsorPrimaryContactName",
            "sponsorPrimaryContactEmail",
            "defaultRequire21CFRPart11ForInformedConsents",
            "patientFriendlyTitle",
            "patientFriendlySummary",
            "benefitsOfEnrollment",
            "risksOfEnrollment",
            "proceduresAndInterventionsText",
            "alternativeProceduresAndTreatments",
            "injuryProtocolText",
            "leavingStudyEarlyEnabled",
            "participationTerminationEnabled",
            "confidentialityOfDataText",
            "compensation",
            "costOfParticipationEnabled",
            "sharingResultsEnabled",
            "hipaaAuthorizationTemplateEnabled",
            "sitePrimaryContactName",
            "sitePrimaryContactEmail",
            "sitePrimaryContactLink",
            "isPrescreenConsentRequired",
            "investigationType",
            "loginTitle",
            "loginText",
            "welcomeTitle",
            "welcomeText",
            "dataUpdateWelcomeTitle",
            "dataUpdateWelcomeText",
            "californiaBillOfRightsTitle",
            "californiaBillOfRightsText",
            "studyInformationTitle",
            "informedConsentPrimaryDiseaseOrConditions",
            "lengthOfStudyNumberOfParticipantsTitle",
            "informedConsentStartDate",
            "informedConsentCompletionDate",
            "informedTargetNumberOfPatients",
            "healthRecordUpdatesTitle",
            "healthRecordUpdatesText",
            "proceduresAndInterventionsTitle",
            "benefitsTitle",
            "risksTitle",
            "voluntaryParticipationTitle",
            "voluntaryParticipationText",
            "injuryProtocolTitle",
            "dataTypesTitle",
            "sensitiveDataTitle",
            "sensitiveDataText",
            "confidentialityMeasuresTitle",
            "whoToContactTitle",
            "whoToContactText",
            "informedConsentTitle",
            "hipaaTitle",
            "hipaaText",
            "hipaaOrganizationLegalName",
            "hipaaOrganizationLegalNameConfirmed",
            "participantTitle",
            "participantInstructionsForGuidedConsent",
            "parentGuardianTitle",
            "parentGuardianInstructions",
            "parentGuardianSignatureTitle",
            "parentGuardianInformedConsentStatement",
            "additionalParentGuardianTitle",
            "additionalParentGuardianInstructions",
            "additionalParentGuardianExemptSignature",
            "additionalParentGuardianExemptSignatureText",
            "additionalParentGuardianSignatureTitle",
            "additionalParentGuardianInformedConsentStatement",
            "minorParticipantTitle",
            "minorParticipantInstructions",
            "minorParticipantExemptFromAssent",
            "minorParticipantExemptFromAssentText",
            "minorParticipantAssentTitle",
            "minorParticipantAssentStatement",
            "witnessTitle",
            "witnessInstructions",
            "witnessSignatureTitle",
            "witnessConfirmation",
            "providerTitle",
            "providerInstructions",
            "providerSignatureTitle",
            "providerConfirmation",
            "informedConsentStatement",
            "thankYouConsentedTitle",
            "thankYouConsentedText",
            "dataUpdateThankYouConsentedTitle",
            "dataUpdateThankYouConsentedText",
            "thankYouDeclinedTitle",
            "thankYouDeclinedText",
            "futureUseOfData",
            "futureUseOfDataTitle",
            "futureUseOfDataText",
        ], (input) => {
            var _a;
            let inputToSave = input;
            if (input.healthRecordsUpdatesEnabled === true) {
                inputToSave = {
                    ...inputToSave,
                    healthRecordsUpdatesOnDemand: (_a = this.studyFormModel.input.healthRecordsUpdatesOnDemand) !== null && _a !== void 0 ? _a : false,
                    healthRecordsUpdatesEvery: this.studyFormModel.input.healthRecordsUpdatesEvery,
                    healthRecordsUpdatesUnit: this.studyFormModel.input.healthRecordsUpdatesUnit,
                };
            }
            if (input.patientRecordRetrievalRecencyEnabled === true) {
                inputToSave = {
                    ...inputToSave,
                    patientRecordRetrievalRecencyEvery: this.studyFormModel.input.patientRecordRetrievalRecencyEvery,
                    patientRecordRetrievalRecencyUnit: this.studyFormModel.input.patientRecordRetrievalRecencyUnit,
                };
            }
            return !this.loadingStudy && this.autoSaveStudy(inputToSave);
        });
        this.numberToString = (value) => {
            if (value === undefined)
                return undefined;
            return value === null ? "" : String(value);
        };
        this.dateToString = (value) => {
            if (value === undefined)
                return undefined;
            return value === null ? "" : toISODateString(value);
        };
        this.nullIfEmpty = (value) => (value === undefined ? undefined : value === "" ? null : value);
        this.formatBoolean = (value) => (value === undefined ? undefined : value);
        this.autoSaveDebounceTimeMS = 500;
        this.autoSaveStudyInput = {};
        this.autoSaveStudyTimer = null;
        this.isAutoSavingStudy = false;
        this.conflictedStudyInput = {};
        this.loadingStudy = false;
        this.pollStudyTimeMS = 60000;
        this.fastPollTimeMS = 10000;
        this.pollStudyTimer = null;
        this.shouldPollStudy = false;
        this.wasInformedConsentLockedByUs = false;
        this.lockInformedConsentKeepaliveMS = 60000;
        this.lockInformedConsentKeepaliveTimer = null;
        this.sessionId = null;
        reaction(() => routingUIManager.currentRoute, async () => {
            this.cleanupBaseStudy();
            await this.refreshStudy();
        }, { fireImmediately: true, equals: routeEquals });
        reaction(() => [this.isRelevantRoute, this.study, this.isAutoSavingStudy, this.fastPoll], async () => {
            await this.initPollingForStudy();
        }, { fireImmediately: true });
    }
    get informedConsentFormModel() {
        return downcastFormModel(this.studyFormModel);
    }
    get studyId() {
        var _a, _b, _c;
        return this.isRelevantRoute
            ? (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.studyId) !== null && _b !== void 0 ? _b : (_c = this.routingUIManager.currentRouteParams) === null || _c === void 0 ? void 0 : _c.projectId
            : undefined;
    }
    get isRelevantRoute() {
        return !!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [NamedRoutes.PROJECTS_ADD_PROJECT]);
    }
    get isVideoTranscoding() {
        var _a, _b;
        if (this.study === NOT_LOADED || !this.study)
            return false;
        const enrollmentTranscodeStatus = (_b = (_a = this.study) === null || _a === void 0 ? void 0 : _a.enrollmentTranscode) === null || _b === void 0 ? void 0 : _b.status;
        return enrollmentTranscodeStatus === TranscodeStatus.UPLOADING || enrollmentTranscodeStatus === TranscodeStatus.TRANSCODING;
    }
    get isPreviewPDFProcessing() {
        var _a;
        if (this.study === NOT_LOADED || !((_a = this.study) === null || _a === void 0 ? void 0 : _a.previewScreenshots))
            return false;
        return this.study.previewScreenshots.some((screenshot) => screenshot.status === ScreenshotMetadataStatus.JOB_QUEUED || screenshot.status === ScreenshotMetadataStatus.IN_PROGRESS);
    }
    get fastPoll() {
        return this.isVideoTranscoding || this.isPreviewPDFProcessing;
    }
    async refreshStudy(opts) {
        if (!this.studyId || !this.authManager.orgId) {
            this.study = NOT_LOADED;
            this.studyExistsButHasNoDraft = NOT_LOADED;
            this.versionNumber = NOT_LOADED;
            return;
        }
        try {
            if (this.studyId) {
                const oldStudy = this.study;
                if (!!(opts === null || opts === void 0 ? void 0 : opts.fullRefresh) || this.study === NOT_LOADED || this.study === null) {
                    const study = await this.studyManager.getStudy(this.studyId, StudyVersionStatus.DRAFT);
                    if (!study) {
                        const activeStudy = await this.studyManager.getStudy(this.studyId, StudyVersionStatus.ACTIVE);
                        this.studyExistsButHasNoDraft = !!activeStudy;
                    }
                    else {
                        this.studyExistsButHasNoDraft = false;
                    }
                    this.versionNumber = await this.studyManager.getStudyDraftVersionNumber(this.studyId);
                    this.study = study;
                }
                else {
                    const studyResponse = await this.studyService.getStudyById(this.studyId, StudyVersionStatus.DRAFT);
                    this.study = { ...this.study, ...studyResponse };
                }
                if (this.study !== null) {
                    this.loadStudy(this.study, oldStudy, !!(opts === null || opts === void 0 ? void 0 : opts.preserveDirty));
                }
            }
        }
        catch (e) {
            console.error("Refresh study failed", e);
        }
    }
    async refreshStudyAndRestartPolling() {
        this.clearPollStudyTimer();
        await this.refreshStudy({ preserveDirty: true });
        this.pollStudy();
    }
    cleanupBaseStudy() {
        this.baseStudy = {};
    }
    loadStudy(studyDto, oldStudyDto, preserveDirty = false, justSavedKeys = []) {
        const newInput = this.getInputFromStudy(studyDto, true);
        const newBaseInput = this.getInputFromStudy(studyDto, false);
        if (preserveDirty) {
            ;
            [...this.autoSaveStudyInputKeys, ...this.studyErrorKeys, ...this.conflictedKeys].forEach((key) => {
                if (key !== "addVisitedPages" && key !== "addVisitedSections")
                    delete newInput[key];
                if (key !== "addVisitedPages" &&
                    key !== "addVisitedSections" &&
                    !justSavedKeys.includes(key) &&
                    !this.conflictedKeys.includes(key)) {
                    delete newBaseInput[key];
                }
            });
        }
        let modifiedNewInput = newInput;
        if (newInput.consentDataSensitivityScopes) {
            modifiedNewInput = {
                ...newInput,
                consentDataSensitivityScopes: newInput.consentDataSensitivityScopes.filter((scope) => scope !== ConsentDataSensitivityScope.NORMAL),
            };
        }
        this.loadingStudy = true;
        try {
            this.studyFormModel.updateInput(modifiedNewInput);
            if (justSavedKeys.length > 0) {
                const baseStudyUpdate = {};
                justSavedKeys.forEach((key) => {
                    if (key !== "addVisitedPages" && key !== "addVisitedSections" && key in newBaseInput) {
                        baseStudyUpdate[key] = newBaseInput[key];
                    }
                });
                this.updateBaseStudy(baseStudyUpdate);
            }
            else {
                this.updateBaseStudy(newBaseInput);
            }
            if (studyDto.informedConsentSectionLocked && !this.wasInformedConsentLockedByUs) {
                const backendSessionId = studyDto.informedConsentLockSessionId;
                const ourSessionId = this.getOrCreateSessionId();
                this.wasInformedConsentLockedByUs = !!(backendSessionId && backendSessionId === ourSessionId);
            }
            this.onStudyLoaded(studyDto, oldStudyDto, justSavedKeys);
        }
        finally {
            this.loadingStudy = false;
        }
    }
    updateBaseStudy(baseInput) {
        var _a;
        if (baseInput.enrollmentVideo && !(typeof baseInput.enrollmentVideo === "object" && "sourceUrl" in baseInput.enrollmentVideo)) {
            throw new Error("baseInput.enrollmentVideo should never be a file");
        }
        this.baseStudy = {
            ...this.baseStudy,
            ...this.toUpdateStudyInput(baseInput, false),
            ...withoutUndefinedProperties({
                hipaaAuthorizationFormUrl: typeof baseInput.hipaaAuthorizationForm === "string" ? baseInput.hipaaAuthorizationForm : undefined,
                orgLogoUrlOnInformedConsent: typeof baseInput.orgLogoOnInformedConsent === "string" ? baseInput.orgLogoOnInformedConsent : undefined,
                organizationIconUrl: typeof baseInput.organizationIconUrl === "string" ? baseInput.organizationIconUrl : undefined,
                enrollmentTranscode: baseInput.enrollmentVideo,
                additionalSections: baseInput.additionalSections,
                studySectionImages: baseInput.studySectionImages,
                timelineVisits: (_a = baseInput.timelineVisits) !== null && _a !== void 0 ? _a : [],
            }),
        };
    }
    updateBaseStudyForFullRecordChange(fullRecordWithoutRestrictedData) {
        this.baseStudy.fullRecordWithoutRestrictedData = fullRecordWithoutRestrictedData;
    }
    getInputFromStudy(studyDto, applyDefaultValues) {
        var _a, _b, _c;
        const formatStringArray = (value, defaultArraySize) => {
            if (value === undefined)
                return undefined;
            return !value || (value.length === 0 && applyDefaultValues) ? Array(defaultArraySize).fill("") : value;
        };
        const defaultValue = (value, defaultValueParam) => {
            if (value === undefined)
                return undefined;
            if (!applyDefaultValues)
                return value;
            return value !== null && value !== void 0 ? value : defaultValueParam;
        };
        const defaultEmptyString = (value) => {
            if (value === undefined)
                return undefined;
            return value !== null && value !== void 0 ? value : "";
        };
        const deepClone = (value) => {
            if (value === undefined)
                return undefined;
            return JSON.parse(JSON.stringify(value));
        };
        const { projectType, studyType, accessData, pdda } = studyDto;
        let projectKey = null;
        try {
            if (projectType && studyType && accessData && pdda) {
                projectKey = getProjectKey(projectType, studyType, accessData, pdda);
            }
        }
        catch (e) {
            projectKey = null;
        }
        const applyDefaultAdditionalSection = projectKey !== ProjectKey.GENERALDATAUSE_IDENTIFIED_PDDE;
        const englishAdditionalSections = studyDto.additionalSections === undefined
            ? undefined
            : studyDto.additionalSections !== null || !applyDefaultValues
                ? deepClone((_b = (_a = studyDto.additionalSections) === null || _a === void 0 ? void 0 : _a.filter((section) => section.language === StudyTranslationLanguage.en)) !== null && _b !== void 0 ? _b : undefined)
                : applyDefaultAdditionalSection
                    ? [{ id: crypto.randomUUID(), title: "", description: "", included: false, language: StudyTranslationLanguage.en }]
                    : [];
        return withoutUndefinedProperties({
            title: defaultEmptyString(studyDto.title),
            briefSummary: defaultEmptyString(studyDto.briefSummary),
            description: defaultEmptyString(studyDto.description),
            clinicalTrialPhase: studyDto.clinicalTrialPhase,
            primaryDiseaseOrConditions: formatStringArray(deepClone(studyDto.primaryDiseaseOrConditions), 1),
            interventions: formatStringArray(deepClone(studyDto.interventions), 1),
            outcomes: defaultEmptyString(studyDto.outcomes),
            inclusionExclusionCriteria: defaultEmptyString(studyDto.inclusionExclusionCriteria),
            fullRecordWithoutRestrictedData: studyDto.fullRecordWithoutRestrictedData,
            consentDataResourceScopes: studyDto.consentDataResourceScopes,
            consentDataSensitivityScopes: studyDto.consentDataSensitivityScopes,
            targetNumberOfPatients: this.numberToString(studyDto.targetNumberOfPatients),
            startDate: this.dateToString(studyDto.startDate),
            completionDate: this.dateToString(studyDto.completionDate),
            primaryCompletionDate: this.dateToString(studyDto.primaryCompletionDate),
            healthRecordsUpdatesEnabled: studyDto.healthRecordsUpdatesEnabled,
            healthRecordsUpdatesOnDemand: studyDto.healthRecordsUpdatesOnDemand,
            healthRecordsUpdatesEvery: this.numberToString(defaultValue(studyDto.healthRecordsUpdatesEvery, 4)),
            healthRecordsUpdatesUnit: defaultValue(studyDto.healthRecordsUpdatesUnit, StudyPeriodicDayAndWeekUnits.WEEK),
            sponsorOrganizationName: defaultEmptyString(studyDto.sponsorOrganizationName),
            sponsorPrimaryContactName: defaultEmptyString(studyDto.sponsorPrimaryContactName),
            sponsorPrimaryContactTitle: defaultEmptyString(studyDto.sponsorPrimaryContactTitle),
            sponsorPrimaryContactEmail: defaultEmptyString(studyDto.sponsorPrimaryContactEmail),
            sponsorPrimaryContactPhone: defaultEmptyString(studyDto.sponsorPrimaryContactPhone),
            sponsorPrimaryContactPhoneExt: defaultEmptyString(studyDto.sponsorPrimaryContactPhoneExt),
            funderType: studyDto.funderType,
            studyIdentifier: defaultEmptyString(studyDto.studyIdentifier),
            webpageLink: defaultEmptyString(studyDto.webpageLink),
            protocolNumber: defaultEmptyString(studyDto.protocolNumber),
            defaultRequire21CFRPart11ForInformedConsents: studyDto.require21CFRPart11ForInformedConsents,
            patientFriendlyTitle: defaultEmptyString(studyDto.patientFriendlyTitle),
            patientFriendlySummary: defaultEmptyString(studyDto.patientFriendlySummary),
            purposeOfResearchText: defaultEmptyString(studyDto.purposeOfResearchText),
            benefitsOfEnrollment: defaultEmptyString(studyDto.benefitsOfEnrollment),
            risksOfEnrollment: defaultEmptyString(studyDto.risksOfEnrollment),
            proceduresAndInterventionsText: defaultEmptyString(studyDto.proceduresAndInterventionsText),
            timelineVisitsEnabled: studyDto.timelineVisitsEnabled,
            timelineVisits: defaultValue(deepClone(studyDto.timelineVisits && studyDto.timelineVisits.length > 0 ? studyDto.timelineVisits : undefined), [
                { period: "", description: "" },
                { period: "", description: "" },
            ]),
            alternativeProceduresAndTreatments: defaultEmptyString(studyDto.alternativeProceduresAndTreatments),
            injuryProtocolText: defaultEmptyString(studyDto.injuryProtocolText),
            leavingStudyEarlyEnabled: studyDto.leavingStudyEarlyEnabled,
            leavingStudyEarlyText: defaultEmptyString(studyDto.leavingStudyEarlyText),
            participationTerminationEnabled: studyDto.participationTerminationEnabled,
            participationTerminationText: defaultEmptyString(studyDto.participationTerminationText),
            futureUseOfDataEnabled: studyDto.futureUseOfDataEnabled,
            futureUseOfData: studyDto.futureUseOfData,
            futureUseOfDataText: defaultEmptyString(studyDto.futureUseOfDataText),
            confidentialityOfDataText: defaultEmptyString(studyDto.confidentialityOfDataText),
            compensation: studyDto.compensation,
            compensationText: defaultEmptyString(studyDto.compensationText),
            costOfParticipationEnabled: studyDto.costOfParticipationEnabled,
            costOfParticipationText: defaultEmptyString(studyDto.costOfParticipationText),
            sharingResultsEnabled: studyDto.sharingResultsEnabled,
            sharingResultsText: defaultEmptyString(studyDto.sharingResultsText),
            additionalSections: englishAdditionalSections,
            sitePrimaryContactName: defaultEmptyString(studyDto.sitePrimaryContactName),
            sitePrimaryContactTitle: defaultEmptyString(studyDto.sitePrimaryContactTitle),
            sitePrimaryContactEmail: defaultEmptyString(studyDto.sitePrimaryContactEmail),
            sitePrimaryContactPhone: defaultEmptyString(studyDto.sitePrimaryContactPhone),
            sitePrimaryContactPhoneExt: defaultEmptyString(studyDto.sitePrimaryContactPhoneExt),
            sitePrimaryContactLink: defaultEmptyString(studyDto.sitePrimaryContactLink),
            icfImportMethod: studyDto.icfImportMethod,
            icfFileName: defaultEmptyString(studyDto.icfFileName),
            studyProtocolFileName: defaultEmptyString(studyDto.studyProtocolFileName),
            hipaaAuthorizationForm: studyDto.hipaaAuthorizationFormUrl,
            hipaaAuthorizationTemplateEnabled: defaultValue(studyDto.hipaaAuthorizationTemplateEnabled, true),
            hipaaAuthorizationText: defaultEmptyString(studyDto.hipaaAuthorizationText),
            patientMRNOnInformedConsent: studyDto.patientMRNOnInformedConsent,
            orgLogoOnInformedConsent: studyDto.orgLogoUrlOnInformedConsent,
            organizationIconUrl: studyDto.organizationIconUrl,
            enrollmentVideo: studyDto.enrollmentTranscode,
            investigationType: studyDto.investigationType === null ? StudyInvestigationType.NEITHER : studyDto.investigationType,
            purposeOfResearchEnabled: studyDto.purposeOfResearchEnabled,
            informedConsentPrimaryDiseaseOrConditions: formatStringArray(deepClone(studyDto.informedConsentPrimaryDiseaseOrConditions), 1),
            informedConsentIRBApprovalStamp: defaultEmptyString(studyDto.informedConsentIRBApprovalStamp),
            informedConsentStartDate: this.dateToString(studyDto.informedConsentStartDate),
            informedConsentCompletionDate: this.dateToString(studyDto.informedConsentCompletionDate),
            informedTargetNumberOfPatients: this.numberToString(studyDto.informedTargetNumberOfPatients),
            alternativesToParticipation: defaultEmptyString(studyDto.alternativesToParticipation),
            informedConsentInterested: defaultValue(studyDto.informedConsentInterested, false),
            loginTitle: defaultEmptyString(studyDto.loginTitle),
            loginText: defaultEmptyString(studyDto.loginText),
            welcomeTitle: defaultEmptyString(studyDto.welcomeTitle),
            welcomeText: defaultEmptyString(studyDto.welcomeText),
            dataUpdateWelcomeTitle: defaultEmptyString(studyDto.dataUpdateWelcomeTitle),
            dataUpdateWelcomeText: defaultEmptyString(studyDto.dataUpdateWelcomeText),
            californiaBillOfRightsTitle: defaultEmptyString(studyDto.californiaBillOfRightsTitle),
            californiaBillOfRightsText: defaultEmptyString(studyDto.californiaBillOfRightsText),
            studyInformationTitle: defaultEmptyString(studyDto.studyInformationTitle),
            purposeOfResearchTitle: defaultEmptyString(studyDto.purposeOfResearchTitle),
            lengthOfStudyNumberOfParticipantsTitle: defaultEmptyString(studyDto.lengthOfStudyNumberOfParticipantsTitle),
            lengthOfStudyNumberOfParticipantsText: defaultEmptyString(studyDto.lengthOfStudyNumberOfParticipantsText),
            proceduresAndInterventionsTitle: defaultEmptyString(studyDto.proceduresAndInterventionsTitle),
            timelineTitle: defaultEmptyString(studyDto.timelineTitle),
            benefitsTitle: defaultEmptyString(studyDto.benefitsTitle),
            risksTitle: defaultEmptyString(studyDto.risksTitle),
            voluntaryParticipationTitle: defaultEmptyString(studyDto.voluntaryParticipationTitle),
            voluntaryParticipationText: defaultEmptyString(studyDto.voluntaryParticipationText),
            withdrawalImplicationsTitle: defaultEmptyString(studyDto.withdrawalImplicationsTitle),
            injuryProtocolTitle: defaultEmptyString(studyDto.injuryProtocolTitle),
            participationTerminationTitle: defaultEmptyString(studyDto.participationTerminationTitle),
            dataTypesTitle: defaultEmptyString(studyDto.dataTypesTitle),
            sensitiveDataTitle: defaultEmptyString(studyDto.sensitiveDataTitle),
            sensitiveDataText: defaultEmptyString(studyDto.sensitiveDataText),
            futureUseOfDataTitle: defaultEmptyString(studyDto.futureUseOfDataTitle),
            confidentialityMeasuresTitle: defaultEmptyString(studyDto.confidentialityMeasuresTitle),
            compensationTitle: defaultEmptyString(studyDto.compensationTitle),
            costTitle: defaultEmptyString(studyDto.costTitle),
            researchResultsTitle: defaultEmptyString(studyDto.researchResultsTitle),
            whoToContactTitle: defaultEmptyString(studyDto.whoToContactTitle),
            whoToContactText: defaultEmptyString(studyDto.whoToContactText),
            informedConsentTitle: defaultEmptyString(studyDto.informedConsentTitle),
            informedConsentText: defaultEmptyString(studyDto.informedConsentText),
            informedConsentYesText: defaultEmptyString(studyDto.informedConsentYesText),
            informedConsentNoText: defaultEmptyString(studyDto.informedConsentNoText),
            informedConsentInterestedText: defaultEmptyString(studyDto.informedConsentInterestedText),
            hipaaTitle: defaultEmptyString(studyDto.hipaaTitle),
            hipaaText: defaultEmptyString(studyDto.hipaaText),
            hipaaOrganizationLegalName: defaultEmptyString(studyDto.hipaaOrganizationLegalName),
            hipaaOrganizationLegalNameConfirmed: defaultValue(studyDto.hipaaOrganizationLegalNameConfirmed, false),
            thankYouConsentedTitle: defaultEmptyString(studyDto.thankYouConsentedTitle),
            thankYouConsentedText: defaultEmptyString(studyDto.thankYouConsentedText),
            dataUpdateThankYouConsentedTitle: defaultEmptyString(studyDto.dataUpdateThankYouConsentedTitle),
            dataUpdateThankYouConsentedText: defaultEmptyString(studyDto.dataUpdateThankYouConsentedText),
            thankYouDeclinedTitle: defaultEmptyString(studyDto.thankYouDeclinedTitle),
            thankYouDeclinedText: defaultEmptyString(studyDto.thankYouDeclinedText),
            thankYouImInterestedTitle: defaultEmptyString(studyDto.thankYouImInterestedTitle),
            thankYouImInterestedText: defaultEmptyString(studyDto.thankYouImInterestedText),
            informedConsentPrimaryDiseaseOrConditionsCopied: formatStringArray(deepClone(studyDto.informedConsentPrimaryDiseaseOrConditionsCopied), 1),
            informedConsentStartDateCopied: this.dateToString(studyDto.informedConsentStartDateCopied),
            informedConsentCompletionDateCopied: this.dateToString(studyDto.informedConsentCompletionDateCopied),
            informedTargetNumberOfPatientsCopied: this.numberToString(studyDto.informedTargetNumberOfPatientsCopied),
            lengthOfStudyEnabled: studyDto.lengthOfStudyEnabled,
            informedHealthRecordUpdatesEnabled: studyDto.informedHealthRecordUpdatesEnabled,
            healthRecordUpdatesTitle: defaultEmptyString(studyDto.healthRecordUpdatesTitle),
            healthRecordUpdatesText: defaultEmptyString(studyDto.healthRecordUpdatesText),
            injuryProtocolEnabled: studyDto.injuryProtocolEnabled,
            interventionsEnabled: studyDto.interventionsEnabled,
            confidentialityMeasuresEnabled: studyDto.confidentialityMeasuresEnabled,
            patientMRNOnParticipantSignature: studyDto.patientMRNOnParticipantSignature,
            minorParticipantConsentEnabled: studyDto.minorParticipantConsentEnabled,
            additionalParentGuardianEnabled: studyDto.additionalParentGuardianEnabled,
            additionalParentGuardianExemptSignature: studyDto.additionalParentGuardianExemptSignature,
            minorParticipantExemptFromAssent: studyDto.minorParticipantExemptFromAssent,
            larConsentEnabled: studyDto.larConsentEnabled,
            larParticipantConsentExemptFromAssent: studyDto.larParticipantConsentExemptFromAssent,
            parentGuardianTitle: defaultEmptyString(studyDto.parentGuardianTitle),
            parentGuardianInstructions: defaultEmptyString(studyDto.parentGuardianInstructions),
            parentGuardianSignatureTitle: defaultEmptyString(studyDto.parentGuardianSignatureTitle),
            parentGuardianInformedConsentStatement: defaultEmptyString(studyDto.parentGuardianInformedConsentStatement),
            additionalParentGuardianTitle: defaultEmptyString(studyDto.additionalParentGuardianTitle),
            additionalParentGuardianInstructions: defaultEmptyString(studyDto.additionalParentGuardianInstructions),
            additionalParentGuardianExemptSignatureText: defaultEmptyString(studyDto.additionalParentGuardianExemptSignatureText),
            additionalParentGuardianSignatureTitle: defaultEmptyString(studyDto.additionalParentGuardianSignatureTitle),
            additionalParentGuardianInformedConsentStatement: defaultEmptyString(studyDto.additionalParentGuardianInformedConsentStatement),
            minorParticipantTitle: defaultEmptyString(studyDto.minorParticipantTitle),
            minorParticipantInstructions: defaultEmptyString(studyDto.minorParticipantInstructions),
            minorParticipantExemptFromAssentText: defaultEmptyString(studyDto.minorParticipantExemptFromAssentText),
            minorParticipantAssentTitle: defaultEmptyString(studyDto.minorParticipantAssentTitle),
            minorParticipantAssentStatement: defaultEmptyString(studyDto.minorParticipantAssentStatement),
            participantTitle: defaultEmptyString(studyDto.participantTitle),
            participantInstructionsForGuidedConsent: defaultEmptyString(studyDto.participantInstructionsForGuidedConsent),
            informedConsentStatement: defaultEmptyString(studyDto.informedConsentStatement),
            larTitle: defaultEmptyString(studyDto.larTitle),
            larInstructions: defaultEmptyString(studyDto.larInstructions),
            larSignatureTitle: defaultEmptyString(studyDto.larSignatureTitle),
            larInformedConsentStatement: defaultEmptyString(studyDto.larInformedConsentStatement),
            larParticipantTitle: defaultEmptyString(studyDto.larParticipantTitle),
            larParticipantInstructions: defaultEmptyString(studyDto.larParticipantInstructions),
            larParticipantExemptFromAssentText: defaultEmptyString(studyDto.larParticipantExemptFromAssentText),
            larParticipantAssentTitle: defaultEmptyString(studyDto.larParticipantAssentTitle),
            larParticipantAssentStatement: defaultEmptyString(studyDto.larParticipantAssentStatement),
            witnessTitle: defaultEmptyString(studyDto.witnessTitle),
            witnessInstructions: defaultEmptyString(studyDto.witnessInstructions),
            witnessSignatureTitle: defaultEmptyString(studyDto.witnessSignatureTitle),
            witnessConfirmation: defaultEmptyString(studyDto.witnessConfirmation),
            providerTitle: defaultEmptyString(studyDto.providerTitle),
            providerInstructions: defaultEmptyString(studyDto.providerInstructions),
            providerSignatureTitle: defaultEmptyString(studyDto.providerSignatureTitle),
            providerConfirmation: defaultEmptyString(studyDto.providerConfirmation),
            studyTranslatableFields: deepClone((_c = studyDto.studyTranslatableFields) === null || _c === void 0 ? void 0 : _c.find((t) => t.language === this.selectedTranslationLanguage)),
            studySectionImages: deepClone(studyDto.studySectionImages),
            projectType: studyDto.projectType,
            suppressNotifications: defaultValue(studyDto.suppressNotifications, false),
            introTitle: defaultEmptyString(studyDto.introTitle),
            introPurposeOfDisclosure: defaultEmptyString(studyDto.introPurposeOfDisclosure),
            introBriefEducation: defaultEmptyString(studyDto.introBriefEducation),
            patientHealthRecordRightsTitle: defaultEmptyString(studyDto.patientHealthRecordRightsTitle),
            contactDetails: defaultEmptyString(studyDto.contactDetails),
            purposeOfDataTitle: defaultEmptyString(studyDto.purposeOfDataTitle),
            purposeOfDataDisclosure: studyDto.purposeOfDataDisclosure,
            purposeOfDataDisclosureOther: defaultEmptyString(studyDto.purposeOfDataDisclosureOther),
            purposeOfDataBriefEducation: defaultEmptyString(studyDto.purposeOfDataBriefEducation),
            patientRecordRetrievalRecencyEnabled: studyDto.patientRecordRetrievalRecencyEnabled,
            patientRecordRetrievalRecencyEvery: this.numberToString(defaultValue(studyDto.patientRecordRetrievalRecencyEvery, 30)),
            patientRecordRetrievalRecencyUnit: defaultValue(studyDto.patientRecordRetrievalRecencyUnit, StudyPeriodicUnit.DAY),
        });
    }
    toUpdateStudyInput(input, validateFields = true) {
        return withoutUndefinedProperties(this._toUpdateStudyInput(input, validateFields));
    }
    _toUpdateStudyInput(input, validateFields) {
        var _a, _b, _c, _d, _e;
        if (validateFields)
            this.validateStudyOnUpdateAndRemoveInvalidInputs(input);
        if (Object.keys(input).length === 0)
            return {};
        const stringToNumber = (value) => value === undefined ? undefined : value === "" || value === null ? null : parseInt(value);
        const stringToDate = (value) => value === undefined ? undefined : value === "" || value === null ? null : new Date(value);
        return {
            importMethod: (_a = input.importMethod) !== null && _a !== void 0 ? _a : undefined,
            studyType: (_b = input.studyType) !== null && _b !== void 0 ? _b : undefined,
            accessData: (_c = input.accessData) !== null && _c !== void 0 ? _c : undefined,
            title: this.nullIfEmpty(input.title),
            briefSummary: this.nullIfEmpty(input.briefSummary),
            description: this.nullIfEmpty(input.description),
            clinicalTrialPhase: input.clinicalTrialPhase,
            primaryDiseaseOrConditions: input.primaryDiseaseOrConditions,
            interventions: input.interventions,
            outcomes: this.nullIfEmpty(input.outcomes),
            inclusionExclusionCriteria: this.nullIfEmpty(input.inclusionExclusionCriteria),
            fullRecordWithoutRestrictedData: input.fullRecordWithoutRestrictedData,
            consentDataResourceScopes: input.consentDataResourceScopes,
            consentDataSensitivityScopes: input.consentDataSensitivityScopes,
            targetNumberOfPatients: stringToNumber(input.targetNumberOfPatients),
            startDate: stringToDate(input.startDate),
            completionDate: stringToDate(input.completionDate),
            primaryCompletionDate: stringToDate(input.primaryCompletionDate),
            healthRecordsUpdatesEnabled: input.healthRecordsUpdatesEnabled,
            healthRecordsUpdatesOnDemand: input.healthRecordsUpdatesOnDemand,
            healthRecordsUpdatesEvery: stringToNumber(input.healthRecordsUpdatesEvery),
            healthRecordsUpdatesUnit: input.healthRecordsUpdatesUnit,
            sponsorOrganizationName: this.nullIfEmpty(input.sponsorOrganizationName),
            sponsorPrimaryContactName: this.nullIfEmpty(input.sponsorPrimaryContactName),
            sponsorPrimaryContactTitle: this.nullIfEmpty(input.sponsorPrimaryContactTitle),
            sponsorPrimaryContactEmail: this.nullIfEmpty(input.sponsorPrimaryContactEmail),
            sponsorPrimaryContactPhone: this.nullIfEmpty(input.sponsorPrimaryContactPhone),
            sponsorPrimaryContactPhoneExt: this.nullIfEmpty(input.sponsorPrimaryContactPhoneExt),
            funderType: input.funderType,
            studyIdentifier: this.nullIfEmpty(input.studyIdentifier),
            webpageLink: this.nullIfEmpty(input.webpageLink),
            protocolNumber: this.nullIfEmpty(input.protocolNumber),
            require21CFRPart11ForInformedConsents: this.formatBoolean(input.defaultRequire21CFRPart11ForInformedConsents),
            icfImportMethod: (_d = input.icfImportMethod) !== null && _d !== void 0 ? _d : undefined,
            icfFileName: this.nullIfEmpty(input.icfFileName),
            studyProtocolFileName: this.nullIfEmpty(input.studyProtocolFileName),
            lastSkippedGenaiInputHash: input.lastSkippedGenaiInputHash,
            patientFriendlyTitle: this.nullIfEmpty(input.patientFriendlyTitle),
            patientFriendlySummary: this.nullIfEmpty(input.patientFriendlySummary),
            purposeOfResearchText: this.nullIfEmpty(input.purposeOfResearchText),
            benefitsOfEnrollment: this.nullIfEmpty(input.benefitsOfEnrollment),
            risksOfEnrollment: this.nullIfEmpty(input.risksOfEnrollment),
            proceduresAndInterventionsText: this.nullIfEmpty(input.proceduresAndInterventionsText),
            timelineVisitsEnabled: input.timelineVisitsEnabled,
            timelineVisits: (_e = input.timelineVisits) !== null && _e !== void 0 ? _e : undefined,
            alternativeProceduresAndTreatments: this.nullIfEmpty(input.alternativeProceduresAndTreatments),
            injuryProtocolText: this.nullIfEmpty(input.injuryProtocolText),
            leavingStudyEarlyEnabled: input.leavingStudyEarlyEnabled,
            leavingStudyEarlyText: this.nullIfEmpty(input.leavingStudyEarlyText),
            participationTerminationEnabled: input.participationTerminationEnabled,
            participationTerminationText: this.nullIfEmpty(input.participationTerminationText),
            futureUseOfDataEnabled: input.futureUseOfDataEnabled,
            futureUseOfData: input.futureUseOfData,
            futureUseOfDataText: this.nullIfEmpty(input.futureUseOfDataText),
            confidentialityOfDataText: this.nullIfEmpty(input.confidentialityOfDataText),
            compensation: input.compensation,
            compensationText: this.nullIfEmpty(input.compensationText),
            costOfParticipationEnabled: input.costOfParticipationEnabled,
            costOfParticipationText: this.nullIfEmpty(input.costOfParticipationText),
            sharingResultsEnabled: input.sharingResultsEnabled,
            sharingResultsText: this.nullIfEmpty(input.sharingResultsText),
            sitePrimaryContactName: this.nullIfEmpty(input.sitePrimaryContactName),
            sitePrimaryContactTitle: this.nullIfEmpty(input.sitePrimaryContactTitle),
            sitePrimaryContactEmail: this.nullIfEmpty(input.sitePrimaryContactEmail),
            sitePrimaryContactPhone: this.nullIfEmpty(input.sitePrimaryContactPhone),
            sitePrimaryContactPhoneExt: this.nullIfEmpty(input.sitePrimaryContactPhoneExt),
            sitePrimaryContactLink: this.nullIfEmpty(input.sitePrimaryContactLink),
            patientMRNOnInformedConsent: input.patientMRNOnInformedConsent,
            addVisitedPages: input.addVisitedPages,
            addVisitedSections: input.addVisitedSections,
            hipaaAuthorizationTemplateEnabled: input.hipaaAuthorizationTemplateEnabled,
            hipaaAuthorizationText: this.nullIfEmpty(input.hipaaAuthorizationText),
            deletePrescreenTranscode: input.deletePrescreenTranscode,
            deleteEnrollmentTranscode: input.deleteEnrollmentTranscode,
            investigationType: input.investigationType === StudyInvestigationType.NEITHER ? null : input.investigationType,
            purposeOfResearchEnabled: input.purposeOfResearchEnabled,
            informedConsentPrimaryDiseaseOrConditions: input.informedConsentPrimaryDiseaseOrConditions,
            informedConsentIRBApprovalStamp: this.nullIfEmpty(input.informedConsentIRBApprovalStamp),
            informedConsentStartDate: stringToDate(input.informedConsentStartDate),
            informedConsentCompletionDate: stringToDate(input.informedConsentCompletionDate),
            informedTargetNumberOfPatients: stringToNumber(input.informedTargetNumberOfPatients),
            alternativesToParticipation: this.nullIfEmpty(input.alternativesToParticipation),
            informedConsentInterested: input.informedConsentInterested,
            loginTitle: this.nullIfEmpty(input.loginTitle),
            loginText: this.nullIfEmpty(input.loginText),
            welcomeTitle: this.nullIfEmpty(input.welcomeTitle),
            welcomeText: this.nullIfEmpty(input.welcomeText),
            dataUpdateWelcomeTitle: this.nullIfEmpty(input.dataUpdateWelcomeTitle),
            dataUpdateWelcomeText: this.nullIfEmpty(input.dataUpdateWelcomeText),
            californiaBillOfRightsTitle: this.nullIfEmpty(input.californiaBillOfRightsTitle),
            californiaBillOfRightsText: this.nullIfEmpty(input.californiaBillOfRightsText),
            studyInformationTitle: this.nullIfEmpty(input.studyInformationTitle),
            purposeOfResearchTitle: this.nullIfEmpty(input.purposeOfResearchTitle),
            lengthOfStudyNumberOfParticipantsTitle: this.nullIfEmpty(input.lengthOfStudyNumberOfParticipantsTitle),
            lengthOfStudyNumberOfParticipantsText: this.nullIfEmpty(input.lengthOfStudyNumberOfParticipantsText),
            proceduresAndInterventionsTitle: this.nullIfEmpty(input.proceduresAndInterventionsTitle),
            timelineTitle: this.nullIfEmpty(input.timelineTitle),
            benefitsTitle: this.nullIfEmpty(input.benefitsTitle),
            risksTitle: this.nullIfEmpty(input.risksTitle),
            voluntaryParticipationTitle: this.nullIfEmpty(input.voluntaryParticipationTitle),
            voluntaryParticipationText: this.nullIfEmpty(input.voluntaryParticipationText),
            withdrawalImplicationsTitle: this.nullIfEmpty(input.withdrawalImplicationsTitle),
            injuryProtocolTitle: this.nullIfEmpty(input.injuryProtocolTitle),
            participationTerminationTitle: this.nullIfEmpty(input.participationTerminationTitle),
            dataTypesTitle: this.nullIfEmpty(input.dataTypesTitle),
            sensitiveDataTitle: this.nullIfEmpty(input.sensitiveDataTitle),
            sensitiveDataText: this.nullIfEmpty(input.sensitiveDataText),
            futureUseOfDataTitle: this.nullIfEmpty(input.futureUseOfDataTitle),
            confidentialityMeasuresTitle: this.nullIfEmpty(input.confidentialityMeasuresTitle),
            compensationTitle: this.nullIfEmpty(input.compensationTitle),
            costTitle: this.nullIfEmpty(input.costTitle),
            researchResultsTitle: this.nullIfEmpty(input.researchResultsTitle),
            whoToContactTitle: this.nullIfEmpty(input.whoToContactTitle),
            whoToContactText: this.nullIfEmpty(input.whoToContactText),
            informedConsentTitle: this.nullIfEmpty(input.informedConsentTitle),
            informedConsentText: this.nullIfEmpty(input.informedConsentText),
            informedConsentYesText: this.nullIfEmpty(input.informedConsentYesText),
            informedConsentNoText: this.nullIfEmpty(input.informedConsentNoText),
            informedConsentInterestedText: this.nullIfEmpty(input.informedConsentInterestedText),
            hipaaTitle: this.nullIfEmpty(input.hipaaTitle),
            hipaaText: this.nullIfEmpty(input.hipaaText),
            hipaaOrganizationLegalName: this.nullIfEmpty(input.hipaaOrganizationLegalName),
            hipaaOrganizationLegalNameConfirmed: input.hipaaOrganizationLegalNameConfirmed,
            thankYouConsentedTitle: this.nullIfEmpty(input.thankYouConsentedTitle),
            thankYouConsentedText: this.nullIfEmpty(input.thankYouConsentedText),
            dataUpdateThankYouConsentedTitle: this.nullIfEmpty(input.dataUpdateThankYouConsentedTitle),
            dataUpdateThankYouConsentedText: this.nullIfEmpty(input.dataUpdateThankYouConsentedText),
            thankYouDeclinedTitle: this.nullIfEmpty(input.thankYouDeclinedTitle),
            thankYouDeclinedText: this.nullIfEmpty(input.thankYouDeclinedText),
            thankYouImInterestedTitle: this.nullIfEmpty(input.thankYouImInterestedTitle),
            thankYouImInterestedText: this.nullIfEmpty(input.thankYouImInterestedText),
            informedConsentPrimaryDiseaseOrConditionsCopied: input.informedConsentPrimaryDiseaseOrConditionsCopied,
            informedConsentStartDateCopied: stringToDate(input.informedConsentStartDateCopied),
            informedConsentCompletionDateCopied: stringToDate(input.informedConsentCompletionDateCopied),
            informedTargetNumberOfPatientsCopied: stringToNumber(input.informedTargetNumberOfPatientsCopied),
            lengthOfStudyEnabled: input.lengthOfStudyEnabled,
            informedHealthRecordUpdatesEnabled: input.informedHealthRecordUpdatesEnabled,
            healthRecordUpdatesTitle: this.nullIfEmpty(input.healthRecordUpdatesTitle),
            healthRecordUpdatesText: this.nullIfEmpty(input.healthRecordUpdatesText),
            injuryProtocolEnabled: input.injuryProtocolEnabled,
            interventionsEnabled: input.interventionsEnabled,
            confidentialityMeasuresEnabled: input.confidentialityMeasuresEnabled,
            patientMRNOnParticipantSignature: input.patientMRNOnParticipantSignature,
            minorParticipantConsentEnabled: input.minorParticipantConsentEnabled,
            additionalParentGuardianEnabled: input.additionalParentGuardianEnabled,
            additionalParentGuardianExemptSignature: input.additionalParentGuardianExemptSignature,
            minorParticipantExemptFromAssent: input.minorParticipantExemptFromAssent,
            larConsentEnabled: input.larConsentEnabled,
            larParticipantConsentExemptFromAssent: input.larParticipantConsentExemptFromAssent,
            parentGuardianTitle: this.nullIfEmpty(input.parentGuardianTitle),
            parentGuardianInstructions: this.nullIfEmpty(input.parentGuardianInstructions),
            parentGuardianSignatureTitle: this.nullIfEmpty(input.parentGuardianSignatureTitle),
            parentGuardianInformedConsentStatement: this.nullIfEmpty(input.parentGuardianInformedConsentStatement),
            additionalParentGuardianTitle: this.nullIfEmpty(input.additionalParentGuardianTitle),
            additionalParentGuardianInstructions: this.nullIfEmpty(input.additionalParentGuardianInstructions),
            additionalParentGuardianExemptSignatureText: this.nullIfEmpty(input.additionalParentGuardianExemptSignatureText),
            additionalParentGuardianSignatureTitle: this.nullIfEmpty(input.additionalParentGuardianSignatureTitle),
            additionalParentGuardianInformedConsentStatement: this.nullIfEmpty(input.additionalParentGuardianInformedConsentStatement),
            minorParticipantTitle: this.nullIfEmpty(input.minorParticipantTitle),
            minorParticipantInstructions: this.nullIfEmpty(input.minorParticipantInstructions),
            minorParticipantExemptFromAssentText: this.nullIfEmpty(input.minorParticipantExemptFromAssentText),
            minorParticipantAssentTitle: this.nullIfEmpty(input.minorParticipantAssentTitle),
            minorParticipantAssentStatement: this.nullIfEmpty(input.minorParticipantAssentStatement),
            participantTitle: this.nullIfEmpty(input.participantTitle),
            participantInstructionsForGuidedConsent: this.nullIfEmpty(input.participantInstructionsForGuidedConsent),
            informedConsentStatement: this.nullIfEmpty(input.informedConsentStatement),
            larTitle: this.nullIfEmpty(input.larTitle),
            larInstructions: this.nullIfEmpty(input.larInstructions),
            larSignatureTitle: this.nullIfEmpty(input.larSignatureTitle),
            larInformedConsentStatement: this.nullIfEmpty(input.larInformedConsentStatement),
            larParticipantTitle: this.nullIfEmpty(input.larParticipantTitle),
            larParticipantInstructions: this.nullIfEmpty(input.larParticipantInstructions),
            larParticipantExemptFromAssentText: this.nullIfEmpty(input.larParticipantExemptFromAssentText),
            larParticipantAssentTitle: this.nullIfEmpty(input.larParticipantAssentTitle),
            larParticipantAssentStatement: this.nullIfEmpty(input.larParticipantAssentStatement),
            witnessTitle: this.nullIfEmpty(input.witnessTitle),
            witnessInstructions: this.nullIfEmpty(input.witnessInstructions),
            witnessSignatureTitle: this.nullIfEmpty(input.witnessSignatureTitle),
            witnessConfirmation: this.nullIfEmpty(input.witnessConfirmation),
            providerTitle: this.nullIfEmpty(input.providerTitle),
            providerInstructions: this.nullIfEmpty(input.providerInstructions),
            providerSignatureTitle: this.nullIfEmpty(input.providerSignatureTitle),
            providerConfirmation: this.nullIfEmpty(input.providerConfirmation),
            studyTranslatableFields: input.studyTranslatableFields
                ? [
                    {
                        ...input.studyTranslatableFields,
                        language: this.selectedTranslationLanguage,
                        completed: undefined,
                    },
                ]
                : undefined,
            suppressNotifications: input.suppressNotifications,
            introTitle: this.nullIfEmpty(input.introTitle),
            introPurposeOfDisclosure: this.nullIfEmpty(input.introPurposeOfDisclosure),
            introBriefEducation: this.nullIfEmpty(input.introBriefEducation),
            patientHealthRecordRightsTitle: this.nullIfEmpty(input.patientHealthRecordRightsTitle),
            contactDetails: this.nullIfEmpty(input.contactDetails),
            purposeOfDataTitle: this.nullIfEmpty(input.purposeOfDataTitle),
            purposeOfDataDisclosure: input.purposeOfDataDisclosure,
            purposeOfDataDisclosureOther: this.nullIfEmpty(input.purposeOfDataDisclosureOther),
            purposeOfDataBriefEducation: this.nullIfEmpty(input.purposeOfDataBriefEducation),
            patientRecordRetrievalRecencyEnabled: input.patientRecordRetrievalRecencyEnabled,
            patientRecordRetrievalRecencyEvery: stringToNumber(input.patientRecordRetrievalRecencyEvery),
            patientRecordRetrievalRecencyUnit: input.patientRecordRetrievalRecencyUnit,
        };
    }
    validateStudyOnUpdateAndRemoveInvalidInputs(input) {
        const { validEmail, validPhone, validURL, positiveNumber, futureDate } = validators;
        const f = this.studyFormModel;
        const isImportMethod = input.importMethod === StudyImportMethod.CLINICAL_TRIALS || input.importMethod === StudyImportMethod.PROTOCOL_DOCUMENT;
        if (isImportMethod) {
            if (input.sponsorPrimaryContactEmail !== undefined)
                f.clearError("sponsorPrimaryContactEmail");
            if (input.sponsorPrimaryContactPhone !== undefined)
                f.clearError("sponsorPrimaryContactPhone");
        }
        else {
            if (input.sponsorPrimaryContactEmail !== undefined) {
                f.clearError("sponsorPrimaryContactEmail");
                f.validateField("sponsorPrimaryContactEmail", validEmail, "Enter a valid email address");
                if (f.hasError("sponsorPrimaryContactEmail"))
                    delete input.sponsorPrimaryContactEmail;
            }
            if (input.sponsorPrimaryContactPhone !== undefined) {
                f.clearError("sponsorPrimaryContactPhone");
                f.validateField("sponsorPrimaryContactPhone", validPhone, "Enter a valid phone number");
                if (f.hasError("sponsorPrimaryContactPhone"))
                    delete input.sponsorPrimaryContactPhone;
            }
            if (input.sitePrimaryContactEmail !== undefined) {
                f.clearError("sitePrimaryContactEmail");
                f.validateField("sitePrimaryContactEmail", validEmail, "Enter a valid email address");
                if (f.hasError("sitePrimaryContactEmail"))
                    delete input.sitePrimaryContactEmail;
            }
            if (input.sitePrimaryContactPhone !== undefined) {
                f.clearError("sitePrimaryContactPhone");
                f.validateField("sitePrimaryContactPhone", validPhone, "Enter a valid phone number");
                if (f.hasError("sitePrimaryContactPhone"))
                    delete input.sitePrimaryContactPhone;
            }
            if (input.sitePrimaryContactLink !== undefined) {
                f.clearError("sitePrimaryContactLink");
                f.validateField("sitePrimaryContactLink", validURL, "Enter a valid URL (e.g. https://example.com)");
                if (f.hasError("sitePrimaryContactLink"))
                    delete input.sitePrimaryContactLink;
            }
        }
        if (input.completionDate !== undefined || input.primaryCompletionDate !== undefined) {
            f.clearError("completionDate");
            f.clearError("primaryCompletionDate");
            if (input.completionDate) {
                f.validateField("completionDate", futureDate, "Completion date must be in the future");
            }
            if (input.primaryCompletionDate) {
                f.validateField("primaryCompletionDate", futureDate, "Primary completion date must be in the future");
            }
            if (!f.hasError("primaryCompletionDate")) {
                if (!!f.input.completionDate &&
                    !!f.input.primaryCompletionDate &&
                    isAfter(parseISO(f.input.primaryCompletionDate), parseISO(f.input.completionDate))) {
                    f.addError("primaryCompletionDate", "Primary completion date must be on or before the completion date");
                }
            }
            if (f.hasError("completionDate"))
                delete input.completionDate;
            if (f.hasError("primaryCompletionDate"))
                delete input.primaryCompletionDate;
        }
        const healthRecordsUpdatesEnabled = input.healthRecordsUpdatesEnabled !== undefined ? input.healthRecordsUpdatesEnabled : f.input.healthRecordsUpdatesEnabled;
        if (healthRecordsUpdatesEnabled === true) {
            if (input.healthRecordsUpdatesEvery !== undefined) {
                f.clearError("healthRecordsUpdatesEvery");
                f.validateField("healthRecordsUpdatesEvery", positiveNumber, "Must be at least 1");
                if (f.hasError("healthRecordsUpdatesEvery"))
                    delete input.healthRecordsUpdatesEvery;
            }
        }
        else {
            f.clearError("healthRecordsUpdatesEvery");
        }
        const patientRecordRetrievalRecencyEnabled = input.patientRecordRetrievalRecencyEnabled !== undefined
            ? input.patientRecordRetrievalRecencyEnabled
            : f.input.patientRecordRetrievalRecencyEnabled;
        if (input.patientRecordRetrievalRecencyEvery !== undefined) {
            f.clearError("patientRecordRetrievalRecencyEvery");
        }
        if (patientRecordRetrievalRecencyEnabled === true) {
            if (input.patientRecordRetrievalRecencyEvery !== undefined) {
                f.validateField("patientRecordRetrievalRecencyEvery", positiveNumber, "Must be at least 1");
                if (f.hasError("patientRecordRetrievalRecencyEvery"))
                    delete input.patientRecordRetrievalRecencyEvery;
            }
        }
        return input;
    }
    get autoSaveStudyInputKeys() {
        return Object.keys(this.autoSaveStudyInput);
    }
    get studyErrorKeys() {
        return Object.keys(this.studyFormModel.errors);
    }
    get conflictedKeys() {
        return Object.keys(this.conflictedStudyInput);
    }
    clearAutoSaveStudyTimer() {
        if (this.autoSaveStudyTimer !== null)
            window.clearTimeout(this.autoSaveStudyTimer);
    }
    combineAutoSaveStudyInput(input1, input2) {
        const customInput = {};
        if (input1.addVisitedPages && input2.addVisitedPages) {
            customInput.addVisitedPages = [...new Set([...input1.addVisitedPages, ...input2.addVisitedPages])];
        }
        if (input1.addVisitedSections && input2.addVisitedSections) {
            customInput.addVisitedSections = [...new Set([...input1.addVisitedSections, ...input2.addVisitedSections])];
        }
        if (input1.studyTranslatableFields && input2.studyTranslatableFields) {
            customInput.studyTranslatableFields = { ...input1.studyTranslatableFields, ...input2.studyTranslatableFields };
        }
        return { ...input1, ...input2, ...customInput };
    }
    async doAutoSaveStudy(force = false) {
        if (!this.studyId || !this.authManager.orgId || !this.study || this.study === NOT_LOADED)
            return;
        if (this.isAutoSavingStudy && !force)
            return;
        this.isAutoSavingStudy = true;
        const autoSaveStudyInputSnapshot = { ...this.autoSaveStudyInput };
        if (Object.keys(autoSaveStudyInputSnapshot).length === 0) {
            this.isAutoSavingStudy = false;
            return;
        }
        this.autoSaveStudyInput = {};
        await this.doSaveStudy(autoSaveStudyInputSnapshot);
        if (Object.keys(this.autoSaveStudyInput).length > 0) {
            await this.doAutoSaveStudy(true);
        }
        this.isAutoSavingStudy = false;
    }
    async doSaveStudy(autoSaveStudyInputSnapshot) {
        var _a, _b;
        if (!this.studyId || !this.authManager.orgId || !this.study || this.study === NOT_LOADED)
            return;
        const autoSaveStudyInputKeysSnapshot = Object.keys(autoSaveStudyInputSnapshot);
        const autoSaveStudyInputKeysSaved = new Set(autoSaveStudyInputKeysSnapshot);
        let updateStudyURLInput;
        try {
            updateStudyURLInput = await this.uploadFiles(autoSaveStudyInputSnapshot);
        }
        catch (e) {
            console.error("Error trying to upload files", e);
            autoSaveStudyInputKeysSnapshot.forEach((key) => {
                if (key !== "addVisitedPages" && key !== "addVisitedSections") {
                    this.studyFormModel.addError(key, "Failed to save");
                    autoSaveStudyInputKeysSaved.delete(key);
                }
            });
            updateStudyURLInput = {};
            this.resetUploadingProgress();
        }
        let modifiedAutoSaveStudyInput = { ...autoSaveStudyInputSnapshot };
        if (modifiedAutoSaveStudyInput.consentDataSensitivityScopes) {
            modifiedAutoSaveStudyInput = {
                ...modifiedAutoSaveStudyInput,
                consentDataSensitivityScopes: [
                    ConsentDataSensitivityScope.NORMAL,
                    ...modifiedAutoSaveStudyInput.consentDataSensitivityScopes,
                ],
            };
        }
        const updateStudyInput = this.toUpdateStudyInput(modifiedAutoSaveStudyInput);
        const combinedUpdateStudyInput = {
            ...updateStudyInput,
            ...updateStudyURLInput,
        };
        const baseStudy = this.getBaseStudyForUpdate(combinedUpdateStudyInput);
        let studyResponse;
        const studySectionImagesUpdate = await this.getStudySectionImagesUpdate(autoSaveStudyInputSnapshot);
        const additionalSectionsUpdate = await this.getAdditionalSectionsUpdate(autoSaveStudyInputSnapshot.additionalSections
            ? {
                additionalSections: [
                    ...((_b = (_a = this.study.additionalSections) === null || _a === void 0 ? void 0 : _a.filter((section) => section.language !== StudyTranslationLanguage.en)) !== null && _b !== void 0 ? _b : []),
                    ...autoSaveStudyInputSnapshot.additionalSections,
                ],
            }
            : {});
        const icfTextSegmentsUpdate = await this.getICFTextSegmentsUpdate(autoSaveStudyInputSnapshot);
        this.onBeforeStudySaved();
        try {
            studyResponse = await this.studyService.updateStudyVersion(this.studyId, this.authManager.orgId, {
                ...combinedUpdateStudyInput,
                additionalSections: additionalSectionsUpdate,
                studySectionImages: studySectionImagesUpdate,
                icfTextSegments: icfTextSegmentsUpdate,
                ...{ baseStudy },
            });
            autoSaveStudyInputKeysSnapshot.forEach((key) => {
                if (key !== "addVisitedPages" && key !== "addVisitedSections" && this.studyFormModel.errors[key] === "Failed to save") {
                    this.studyFormModel.clearError(key);
                }
            });
        }
        catch (e) {
            console.error("Error trying to auto save study", e);
            autoSaveStudyInputKeysSnapshot.forEach((key) => {
                if (key !== "addVisitedPages" && key !== "addVisitedSections") {
                    this.studyFormModel.addError(key, "Failed to save");
                    autoSaveStudyInputKeysSaved.delete(key);
                }
            });
            studyResponse = null;
        }
        finally {
            this.resetUploadingProgress();
        }
        if (isErr(studyResponse)) {
            this.conflictedStudyInput = this.getInputFromStudy(studyResponse.error.conflicts, false);
        }
        this.conflictedKeys.forEach((key) => autoSaveStudyInputKeysSaved.delete(key));
        if (!isErr(studyResponse) && studyResponse) {
            const oldStudy = this.study;
            this.study = { ...this.study, ...studyResponse };
            this.loadStudy(this.study, oldStudy, true, Array.from(autoSaveStudyInputKeysSaved));
            this.onStudySaved(combinedUpdateStudyInput, studySectionImagesUpdate, additionalSectionsUpdate, icfTextSegmentsUpdate);
        }
    }
    getBaseStudyForUpdate(updateStudyInput) {
        const updateStudyInputKeys = Object.keys(updateStudyInput);
        const mappedKeys = updateStudyInputKeys
            .flatMap((key) => {
            if (key === "addVisitedPages")
                return [];
            if (key === "addVisitedSections")
                return [];
            if (key === "deletePrescreenTranscode")
                return [];
            if (key === "deleteEnrollmentTranscode")
                return [];
            if (key === "studyTranslatableFields")
                return [];
            return [key];
        })
            .filter((key) => key in this.baseStudy);
        let baseStudyTranslatableFields;
        if (updateStudyInput.studyTranslatableFields && this.baseStudy.studyTranslatableFields) {
            baseStudyTranslatableFields = filterNull(updateStudyInput.studyTranslatableFields.map((updateEntry) => {
                var _a;
                const baseEntry = (_a = this.baseStudy.studyTranslatableFields) === null || _a === void 0 ? void 0 : _a.find((candidate) => candidate.language === updateEntry.language);
                if (!baseEntry)
                    return null;
                const updateEntryKeys = Object.keys(updateEntry);
                return pick(baseEntry, ...updateEntryKeys);
            }));
        }
        else {
            baseStudyTranslatableFields = undefined;
        }
        return withoutUndefinedProperties({
            ...pick(this.baseStudy, ...mappedKeys),
            studyTranslatableFields: baseStudyTranslatableFields,
        });
    }
    resetUploadingProgress() {
        if (this.enrollmentVideoUploadingInfo)
            this.enrollmentVideoUploadingInfo.progress = null;
    }
    async uploadFiles(autoSaveInput) {
        var _a, _b;
        if (!this.studyId || !this.authManager.orgId)
            return {};
        const urlUpdates = {};
        if (autoSaveInput.hipaaAuthorizationForm instanceof File) {
            const fileUploadInfo = await this.studyService.getHipaaAuthorizationFormUploadUrl(this.authManager.orgId, this.studyId, autoSaveInput.hipaaAuthorizationForm);
            await this.provisioningService.uploadFileToUrl(fileUploadInfo.signedUrl, autoSaveInput.hipaaAuthorizationForm);
            urlUpdates.hipaaAuthorizationFormUrl = fileUploadInfo.fileUrl;
        }
        if (autoSaveInput.orgLogoOnInformedConsent instanceof File) {
            const fileUploadInfo = await this.studyService.updateOrgLogoUrlForInformedConsent(this.authManager.orgId, this.studyId, autoSaveInput.orgLogoOnInformedConsent);
            await this.provisioningService.uploadFileToUrl(fileUploadInfo.signedUrl, autoSaveInput.orgLogoOnInformedConsent);
            urlUpdates.orgLogoUrlOnInformedConsent = fileUploadInfo.fileUrl;
        }
        if (autoSaveInput.organizationIconUrl instanceof File) {
            const fileUploadInfo = await this.studyService.getOrgIconUrl(this.authManager.orgId, this.studyId, autoSaveInput.organizationIconUrl);
            await this.provisioningService.uploadFileToUrl(fileUploadInfo.signedUrl, autoSaveInput.organizationIconUrl);
            urlUpdates.organizationIconUrl = fileUploadInfo.fileUrl;
        }
        if (autoSaveInput.enrollmentVideo instanceof File) {
            this.enrollmentVideoUploadingInfo = { progress: 0 };
            const fileUploadInfo = await this.studyService.getEnrollmentVideoUploadUrl(this.authManager.orgId, this.studyId, new TranscodeInput({
                filename: autoSaveInput.enrollmentVideo.name,
                baseTranscode: { id: (_b = (_a = this.baseStudy.enrollmentTranscode) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null },
                language: StudyTranslationLanguage.en,
            }));
            if (isErr(fileUploadInfo)) {
                this.conflictedStudyInput.enrollmentVideo = fileUploadInfo.error.conflict;
            }
            else {
                if (this.enrollmentVideoUploadingInfo)
                    this.enrollmentVideoUploadingInfo.signedUrl = fileUploadInfo.signedUrl;
                await this.provisioningService.uploadFileToUrl(fileUploadInfo.signedUrl, autoSaveInput.enrollmentVideo, (progressPercentage) => {
                    if (this.enrollmentVideoUploadingInfo)
                        this.enrollmentVideoUploadingInfo.progress = progressPercentage;
                });
            }
        }
        return urlUpdates;
    }
    async getAdditionalSectionsUpdate(autoSaveInput) {
        var _a, _b, _c;
        if (!this.study || this.study === NOT_LOADED)
            return undefined;
        if (!autoSaveInput.additionalSections)
            return undefined;
        const existingSectionIndex = indexBy((_a = this.study.additionalSections) !== null && _a !== void 0 ? _a : [], (section) => section.id);
        const baseSectionIndex = indexBy((_b = this.baseStudy.additionalSections) !== null && _b !== void 0 ? _b : [], (section) => section.id);
        const currentSectionIds = new Set(autoSaveInput.additionalSections.map((section) => section.id));
        const updates = filterNull(await Promise.all([
            ...autoSaveInput.additionalSections.map(async (section) => {
                const existing = existingSectionIndex[section.id];
                if (!existing) {
                    return omit(section, "id");
                }
                else if (existing.title !== section.title ||
                    existing.description !== section.description ||
                    existing.included !== section.included ||
                    existing.language !== section.language ||
                    existing.translatedFrom !== section.translatedFrom) {
                    return { ...section, baseAdditionalSection: baseSectionIndex[section.id] };
                }
                else {
                    return null;
                }
            }),
            ...((_c = this.study.additionalSections) !== null && _c !== void 0 ? _c : []).map(async (section) => {
                if (!currentSectionIds.has(section.id)) {
                    return { id: section.id, delete: true };
                }
                return null;
            }),
        ]));
        return updates;
    }
    async getStudySectionImagesUpdate(autoSaveInput) {
        var _a, _b;
        if (!this.study || this.study === NOT_LOADED)
            return undefined;
        if (!autoSaveInput.studySectionImages)
            return undefined;
        try {
            const currentImages = autoSaveInput.studySectionImages.flatMap((sectionGroup) => { var _a; return (_a = sectionGroup === null || sectionGroup === void 0 ? void 0 : sectionGroup.images) !== null && _a !== void 0 ? _a : []; });
            const existingImages = ((_a = this.study.studySectionImages) !== null && _a !== void 0 ? _a : []).flatMap((sectionGroup) => { var _a; return (_a = sectionGroup === null || sectionGroup === void 0 ? void 0 : sectionGroup.images) !== null && _a !== void 0 ? _a : []; });
            const baseImages = ((_b = this.baseStudy.studySectionImages) !== null && _b !== void 0 ? _b : []).flatMap((sectionGroup) => { var _a; return (_a = sectionGroup === null || sectionGroup === void 0 ? void 0 : sectionGroup.images) !== null && _a !== void 0 ? _a : []; });
            const existingImageIndex = indexBy(existingImages.filter((image) => image.id), (image) => image.id);
            const baseImageIndex = indexBy(baseImages.filter((image) => image.id), (image) => image.id);
            const updates = filterNull(await Promise.all(currentImages.map(async (image) => {
                if (!image.id) {
                    return { ...image, baseSectionImage: undefined };
                }
                const existing = existingImageIndex[image.id];
                const base = baseImageIndex[image.id];
                if (existing &&
                    base &&
                    (base.imageUrl !== image.imageUrl ||
                        base.caption !== image.caption ||
                        base.sectionName !== image.sectionName ||
                        base.language !== image.language ||
                        base.status !== image.status ||
                        base.order !== image.order ||
                        base.sectionType !== image.sectionType ||
                        base.additionalSectionId !== image.additionalSectionId ||
                        base.translatedFrom !== image.translatedFrom)) {
                    return { ...image, baseSectionImage: base };
                }
                if (existing && !base) {
                    return { ...image, baseSectionImage: existing };
                }
                if (!existing) {
                    return { ...image, baseSectionImage: undefined };
                }
                return null;
            })));
            return updates.length > 0 ? updates : undefined;
        }
        catch (error) {
            console.error("Error in getStudySectionImagesUpdate:", error);
            return undefined;
        }
    }
    async getICFTextSegmentsUpdate(autoSaveInput) {
        if (!autoSaveInput.icfTextSegments)
            return undefined;
        return autoSaveInput.icfTextSegments;
    }
    async autoSaveStudy(input, quickSave = false) {
        this.clearAutoSaveStudyTimer();
        this.autoSaveStudyInput = this.combineAutoSaveStudyInput(this.autoSaveStudyInput, input);
        if (this.isAutoSavingStudy)
            return;
        if (quickSave) {
            await this.doAutoSaveStudy();
        }
        else {
            this.autoSaveStudyTimer = window.setTimeout(async () => await this.doAutoSaveStudy(), this.autoSaveDebounceTimeMS);
        }
    }
    async saveInformedConsent() {
        await this.saveStudyIfNeeded();
    }
    async saveStudyIfNeeded() {
        await this.autoSaveStudy({}, true);
        try {
            await when(() => !this.isAutoSavingStudy, { timeout: 3000 });
        }
        catch (e) {
            console.error("save study timeout");
        }
    }
    clearPollStudyTimer() {
        if (this.pollStudyTimer !== null)
            window.clearTimeout(this.pollStudyTimer);
    }
    async initPollingForStudy() {
        const prevShouldPollStudy = this.shouldPollStudy;
        this.shouldPollStudy = this.isRelevantRoute && this.study !== NOT_LOADED && this.study !== null && !this.isAutoSavingStudy;
        if (!this.shouldPollStudy) {
            this.clearPollStudyTimer();
        }
        else if (!prevShouldPollStudy && this.shouldPollStudy) {
            this.pollStudy();
        }
    }
    pollStudy() {
        this.clearPollStudyTimer();
        if (!this.shouldPollStudy)
            return;
        this.pollStudyTimer = window.setTimeout(async () => {
            if (this.shouldPollStudy) {
                await this.refreshStudy({ preserveDirty: true });
                this.pollStudy();
            }
        }, this.fastPoll ? this.fastPollTimeMS : this.pollStudyTimeMS);
    }
    getOrCreateSessionId() {
        if (!this.sessionId) {
            const storageKey = `sessionId_${this.studyId}_${this.authManager.id}`;
            const storedSessionId = sessionStorage.getItem(storageKey);
            if (storedSessionId) {
                this.sessionId = storedSessionId;
            }
            else {
                this.sessionId = crypto.randomUUID();
                sessionStorage.setItem(storageKey, this.sessionId);
            }
        }
        return this.sessionId;
    }
    async updateInformedConsentLocked(shouldInformedConsentBeLockedByUs) {
        if (!this.studyId || !this.authManager.orgId)
            return;
        const sessionId = this.getOrCreateSessionId();
        await this.studyService.updateStudyVersion(this.studyId, this.authManager.orgId, {
            informedConsentSectionLocked: shouldInformedConsentBeLockedByUs(),
            informedConsentLockSessionId: shouldInformedConsentBeLockedByUs() ? sessionId : null,
        });
        transaction(() => {
            if (this.study !== NOT_LOADED && this.study !== null) {
                this.study.informedConsentSectionLocked = shouldInformedConsentBeLockedByUs();
                this.study.informedConsentLockSessionId = shouldInformedConsentBeLockedByUs() ? sessionId : null;
            }
            this.wasInformedConsentLockedByUs = shouldInformedConsentBeLockedByUs();
        });
        if (this.lockInformedConsentKeepaliveTimer !== null) {
            window.clearTimeout(this.lockInformedConsentKeepaliveTimer);
        }
        if (shouldInformedConsentBeLockedByUs()) {
            this.lockInformedConsentKeepaliveTimer = window.setTimeout(() => this.updateInformedConsentLocked(shouldInformedConsentBeLockedByUs), this.lockInformedConsentKeepaliveMS);
        }
    }
    get informedConsentSectionLockedByOtherUser() {
        if (this.study === NOT_LOADED ||
            this.study === null ||
            !this.study.informedConsentSectionLocked ||
            this.wasInformedConsentLockedByUs) {
            return false;
        }
        return this.study.informedConsentLockSessionId !== this.getOrCreateSessionId();
    }
    get selectedTranslationLanguage() {
        var _a;
        return (_a = this._selectedTranslationLanguage) !== null && _a !== void 0 ? _a : StudyTranslationLanguage.es;
    }
    updateSelectedTranslationLanguage(language) {
        this._selectedTranslationLanguage = language;
    }
    async keepTheirStudyChanges(key, sectionId) {
        if (key === "additionalSections") {
            if (!sectionId)
                throw new Error("sectionId required for additionalSections");
            await this.keepTheirAdditionalSectionChanges(sectionId);
            return;
        }
        if (key === "studySectionImages") {
            if (!sectionId)
                throw new Error("imageId required for studySectionImages");
            await this.keepTheirStudySectionImageChanges(sectionId);
            return;
        }
        if (key === "studyTranslatableFields") {
            if (!sectionId)
                throw new Error("entry property required for studyTranslatableFields");
            await this.keepTheirStudyTranslatableFieldsChanges(sectionId);
            return;
        }
        delete this.conflictedStudyInput[key];
        await this.refreshStudy({ preserveDirty: true });
    }
    async keepTheirAdditionalSectionChanges(sectionId) {
        var _a, _b;
        const updatedConflicts = (_b = (_a = this.conflictedStudyInput.additionalSections) === null || _a === void 0 ? void 0 : _a.filter((section) => section.id !== sectionId)) !== null && _b !== void 0 ? _b : [];
        if (updatedConflicts.length) {
            this.conflictedStudyInput.additionalSections = updatedConflicts;
        }
        else {
            delete this.conflictedStudyInput.additionalSections;
        }
        await this.refreshStudy({ preserveDirty: true });
    }
    async keepTheirStudySectionImageChanges(imageId) {
        var _a, _b;
        const updatedConflicts = (_b = (_a = this.conflictedStudyInput.studySectionImages) === null || _a === void 0 ? void 0 : _a.filter((image) => image.id !== imageId)) !== null && _b !== void 0 ? _b : [];
        if (updatedConflicts.length) {
            this.conflictedStudyInput.studySectionImages = updatedConflicts;
        }
        else {
            delete this.conflictedStudyInput.studySectionImages;
        }
        await this.refreshStudy({ preserveDirty: true });
    }
    async keepTheirStudyTranslatableFieldsChanges(sectionId) {
        if (!this.conflictedStudyInput.studyTranslatableFields)
            throw new Error("studyTranslatableFields should have been in conflict");
        delete this.conflictedStudyInput.studyTranslatableFields[sectionId];
        if (Object.keys(this.conflictedStudyInput.studyTranslatableFields).filter((key) => key !== "language").length === 0) {
            delete this.conflictedStudyInput.studyTranslatableFields;
        }
        await this.refreshStudy({ preserveDirty: true });
    }
    async keepMyStudyChanges(key, sectionId) {
        if (key === "additionalSections") {
            if (!sectionId)
                throw new Error("sectionId required for additionalSections");
            await this.keepMyAdditionalSectionChanges(sectionId);
            return;
        }
        if (key === "studySectionImages") {
            if (!sectionId)
                throw new Error("imageId required for studySectionImages");
            await this.keepMyStudySectionImageChanges(sectionId);
            return;
        }
        if (key === "studyTranslatableFields") {
            if (!sectionId)
                throw new Error("entry property required for studyTranslatableFields");
            await this.keepMyStudyTranslatableFieldsChanges(sectionId);
            return;
        }
        this.updateBaseStudy(pick(this.conflictedStudyInput, key));
        delete this.conflictedStudyInput[key];
        await this.doSaveStudy(pick(this.studyFormModel.input, key));
    }
    async keepMyAdditionalSectionChanges(sectionId) {
        var _a, _b, _c, _d, _e, _f;
        const conflictedSection = (_a = this.conflictedStudyInput.additionalSections) === null || _a === void 0 ? void 0 : _a.find((section) => section.id === sectionId);
        if (!conflictedSection) {
            console.log("Cannot find section to update");
            return;
        }
        const updatedConflicts = (_c = (_b = this.conflictedStudyInput.additionalSections) === null || _b === void 0 ? void 0 : _b.filter((section) => section.id !== sectionId)) !== null && _c !== void 0 ? _c : [];
        if (updatedConflicts.length) {
            this.conflictedStudyInput.additionalSections = updatedConflicts;
        }
        else {
            delete this.conflictedStudyInput.additionalSections;
        }
        const sectionIndex = (_e = (_d = this.baseStudy.additionalSections) === null || _d === void 0 ? void 0 : _d.findIndex((section) => section.id === sectionId)) !== null && _e !== void 0 ? _e : -1;
        if (sectionIndex === -1) {
            console.log("Cannot find section to update");
            return;
        }
        (_f = this.baseStudy.additionalSections) === null || _f === void 0 ? void 0 : _f.splice(sectionIndex, 1, {
            ...this.baseStudy.additionalSections[sectionIndex],
            ...conflictedSection,
        });
        await this.doSaveStudy(pick(this.studyFormModel.input, "additionalSections"));
    }
    async keepMyStudySectionImageChanges(imageId) {
        var _a, _b, _c;
        const conflictedImage = (_a = this.conflictedStudyInput.studySectionImages) === null || _a === void 0 ? void 0 : _a.find((image) => image.id === imageId);
        if (!conflictedImage) {
            console.log("Cannot find image to update");
            return;
        }
        const updatedConflicts = (_c = (_b = this.conflictedStudyInput.studySectionImages) === null || _b === void 0 ? void 0 : _b.filter((image) => image.id !== imageId)) !== null && _c !== void 0 ? _c : [];
        if (updatedConflicts.length) {
            this.conflictedStudyInput.studySectionImages = updatedConflicts;
        }
        else {
            delete this.conflictedStudyInput.studySectionImages;
        }
        let foundImage = false;
        if (this.baseStudy.studySectionImages) {
            for (const sectionGroup of this.baseStudy.studySectionImages) {
                const imageIndex = sectionGroup.images.findIndex((image) => image.id === imageId);
                if (imageIndex !== -1) {
                    sectionGroup.images.splice(imageIndex, 1, {
                        ...sectionGroup.images[imageIndex],
                        ...conflictedImage,
                    });
                    foundImage = true;
                    break;
                }
            }
        }
        if (!foundImage) {
            console.log("Cannot find image to update in baseStudy");
            return;
        }
        await this.doSaveStudy(pick(this.studyFormModel.input, "studySectionImages"));
    }
    async keepMyStudyTranslatableFieldsChanges(sectionId) {
        if (!this.conflictedStudyInput.studyTranslatableFields)
            throw new Error("studyTranslatableFields should have been in conflict");
        this.updateBaseStudy({
            studyTranslatableFields: pick(this.conflictedStudyInput.studyTranslatableFields, sectionId),
        });
        delete this.conflictedStudyInput.studyTranslatableFields[sectionId];
        if (Object.keys(this.conflictedStudyInput.studyTranslatableFields).filter((key) => key !== "language").length === 0) {
            delete this.conflictedStudyInput.studyTranslatableFields;
        }
        await this.doSaveStudy(pick(this.studyFormModel.input, "studyTranslatableFields"));
    }
    onStudyLoaded(_studyDto, _oldStudyDto, _justSavedKeys = []) {
    }
    onBeforeStudySaved() {
    }
    onStudySaved(_combinedUpdateStudyInput, _studySectionImagesUpdate, _additionalSectionsUpdate, _icfTextSegmentsUpdate) {
    }
}
__decorate([
    observable
], AddProjectFormUIManager.prototype, "study", void 0);
__decorate([
    observable
], AddProjectFormUIManager.prototype, "studyExistsButHasNoDraft", void 0);
__decorate([
    observable
], AddProjectFormUIManager.prototype, "versionNumber", void 0);
__decorate([
    observable
], AddProjectFormUIManager.prototype, "enrollmentVideoUploadingInfo", void 0);
__decorate([
    observable
], AddProjectFormUIManager.prototype, "baseStudy", void 0);
__decorate([
    observable
], AddProjectFormUIManager.prototype, "_selectedTranslationLanguage", void 0);
__decorate([
    computed
], AddProjectFormUIManager.prototype, "studyId", null);
__decorate([
    computed
], AddProjectFormUIManager.prototype, "isRelevantRoute", null);
__decorate([
    computed
], AddProjectFormUIManager.prototype, "isVideoTranscoding", null);
__decorate([
    computed
], AddProjectFormUIManager.prototype, "isPreviewPDFProcessing", null);
__decorate([
    computed
], AddProjectFormUIManager.prototype, "fastPoll", null);
__decorate([
    observable
], AddProjectFormUIManager.prototype, "isAutoSavingStudy", void 0);
__decorate([
    observable
], AddProjectFormUIManager.prototype, "conflictedStudyInput", void 0);
__decorate([
    observable
], AddProjectFormUIManager.prototype, "wasInformedConsentLockedByUs", void 0);
__decorate([
    computed
], AddProjectFormUIManager.prototype, "informedConsentSectionLockedByOtherUser", null);
__decorate([
    computed
], AddProjectFormUIManager.prototype, "selectedTranslationLanguage", null);
